import React, { useState, useEffect } from 'react';
import { nurseAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { toast } from 'react-toastify';
import { Video, ChevronLeft, ChevronRight } from 'lucide-react';
import SessionsTableLoader from '../../components/SessionsTableLoader';

const Sessions = () => {
  const [active, setActive] = useState('Today');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [sortBy, setSortBy] = useState('Upcoming');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [updatingSession, setUpdatingSession] = useState(null);
  // Add new states for notes popup
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionNotes, setSessionNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showingAddNotesFor, setShowingAddNotesFor] = useState(null);
  
  // Add pagination state for main sessions table
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalSessions: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchSessions();
  }, [active, statusFilter, debouncedSearchTerm, sortBy]);

  const fetchSessions = async (page = 1) => {
    try {
      setLoading(true);
      const user = tokenManager.getUser();
      if (!user || !user.id) {
        toast.error('User information not found');
        return;
      }

      // Calculate date range based on active filter
      let dateFrom, dateTo;
      const today = new Date();
      // Ensure we're working with dates in local timezone
      today.setHours(0, 0, 0, 0);

      if (active === 'Today') {
        // Format date in YYYY-MM-DD format using local timezone
        dateFrom = today.toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD format
        dateTo = dateFrom;
      } else if (active === 'This Week') {
        const firstDay = new Date(today);
        firstDay.setDate(today.getDate() - today.getDay()); // Get Sunday
        const lastDay = new Date(firstDay);
        lastDay.setDate(firstDay.getDate() + 6); // Get Saturday
        dateFrom = firstDay.toLocaleDateString('en-CA');
        dateTo = lastDay.toLocaleDateString('en-CA');
      } else if (active === 'This Month') {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        dateFrom = firstDay.toLocaleDateString('en-CA');
        dateTo = lastDay.toLocaleDateString('en-CA');
      } else if (active === 'Past Sessions') {
        // For past sessions, don't send date parameters - API will return all past sessions
        dateFrom = null;
        dateTo = null;
      }

      const params = {
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(statusFilter !== 'All Status' && { status: statusFilter.toLowerCase() }),
        ...(debouncedSearchTerm && { patientName: debouncedSearchTerm }),
        sortBy: sortBy.toLowerCase(),
        page,
        limit: pagination.limit
      };

      const response = await nurseAPI.getNurseSchedule(user.id, params);
      
      // Update pagination if response includes pagination data
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          currentPage: parseInt(response.pagination.currentPage) || 1,
          totalPages: parseInt(response.pagination.totalPages) || 1,
          totalSessions: parseInt(response.pagination.totalSessions) || 0,
          hasNextPage: parseInt(response.pagination.currentPage) < parseInt(response.pagination.totalPages),
          hasPrevPage: parseInt(response.pagination.currentPage) > 1
        }));
      }
      
      const formattedSessions = response.sessions.map(session => ({
        _id: session._id,
        startTime: session.startTime || 'N/A',
        endTime: session.endTime || 'N/A',
        patientName: session.patientName || session.patientId?.name || 'Unknown Patient',
        sessionType: session.sessionType || 'monthly_checkin',
        status: session.status || 'scheduled',
        date: new Date(session.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        sessionMode: session.sessionMode || 'video',
        aiReviewed: session.aiReviewed || false,
        zoomStartUrl: session.zoomStartUrl,
        zoomJoinUrl: session.zoomJoinUrl,
        monthlyAssessmentCompleted: session.monthlyAssessmentCompleted || false,
        totalSessions: session.totalSessions || 1,
        sendMail: session.sendMail || false,
        durationMinutes: session.durationMinutes || 10,
        isSessionActive: checkSessionActive(session)
      }));

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle pagination page changes
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    fetchSessions(newPage);
  };

  const checkSessionActive = (session) => {
    const now = new Date();
    const sessionDate = new Date(session.date);
    const [time, period] = session.startTime.split(' ');
    const [hours, minutes] = time.split(':');
    let sessionHours = parseInt(hours);
    
    if (period === 'PM' && sessionHours !== 12) {
      sessionHours += 12;
    } else if (period === 'AM' && sessionHours === 12) {
      sessionHours = 0;
    }
    
    const sessionDateTime = new Date(sessionDate);
    sessionDateTime.setHours(sessionHours, parseInt(minutes), 0, 0);
    
    // Session is active 10 minutes before start time
    const activeStartTime = new Date(sessionDateTime.getTime() - 10 * 60 * 1000);
    const activeEndTime = new Date(sessionDateTime.getTime() + session.durationMinutes * 60 * 1000);
    
    return now >= activeStartTime && now <= activeEndTime;
  };

  const handleStartSession = async (session) => {
    try {
      setUpdatingSession(session._id);
      const response = await nurseAPI.updateSessionStatus(session._id, 'in_progress');
      if (response.success) {
        toast.success('Session started successfully');
        fetchSessions();
      } else {
        toast.error('Failed to start session');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start session');
    } finally {
      setUpdatingSession(null);
    }
  };

  const handleCompleteSession = async (session) => {
    try {
      setUpdatingSession(session._id);
      const response = await nurseAPI.completeNurseSession(session._id);
      if (response.success) {
        toast.success('Session completed successfully');
        fetchSessions();
      } else {
        toast.error('Failed to complete session');
      }
    } catch (error) {
      console.error('Error completing session:', error);
      toast.error('Failed to complete session');
    } finally {
      setUpdatingSession(null);
    }
  };

  const updateSessionStatus = async (sessionId, status) => {
    try {
      const response = await nurseAPI.updateSessionStatus(sessionId, status);
      if (response.success) {
        toast.success(`Session ${status} successfully`);
        fetchSessions();
      } else {
        toast.error(`Failed to ${status} session`);
      }
    } catch (error) {
      console.error(`Error updating session status to ${status}:`, error);
      toast.error(`Failed to ${status} session`);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedSession || !sessionNotes.trim()) return;
    
    try {
      setSavingNotes(true);
      const response = await nurseAPI.submitSessionFeedback(selectedSession._id, {
        content: sessionNotes,
        type: 'session_notes'
      });
      
      if (response.success) {
        toast.success('Session notes saved successfully');
        setShowNotesPopup(false);
        setSelectedSession(null);
        setSessionNotes('');
        setShowingAddNotesFor(null);
        fetchSessions();
      } else {
        toast.error('Failed to save session notes');
      }
    } catch (error) {
      console.error('Error saving session notes:', error);
      toast.error('Failed to save session notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatSessionType = (type) => {
    if (!type) return 'N/A';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const canJoinSession = (session) => {
    // This is a placeholder. In a real app, you'd check if the session is in 'scheduled' or 'in_progress' status
    // and if the current user is the nurse assigned to the session.
    // For now, we'll assume any session can be joined if it's video and not already in progress.
    return session.sessionMode === 'video' && session.status !== 'in_progress';
  };

  const handleJoinSession = (session) => {
    if (canJoinSession(session)) {
      window.location.href = `/nurse/video-call/${session._id}`;
    } else {
      toast.info('You cannot join this session right now.');
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'missed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Sessions</h1>
        <p className="text-gray-600 mt-2">Track your session history, progress, and upcoming appointments in one place.</p>
      </div>

      <div className="flex justify-between items-center mb-6">
        {/* Time Range Tabs */}
        <div className="inline-flex rounded-md shadow-sm bg-white border border-gray-200" role="group">
          <button
            onClick={() => setActive('Today')}
            className={`px-4 py-2 text-sm font-medium ${
              active === 'Today'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            } ${active === 'Today' ? '' : 'border-r border-gray-200'}`}
          >
            Today
          </button>
          <button
            onClick={() => setActive('This Week')}
            className={`px-4 py-2 text-sm font-medium ${
              active === 'This Week'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            } ${active === 'This Week' ? '' : 'border-r border-gray-200'}`}
          >
            This Week
          </button>
          <button
            onClick={() => setActive('This Month')}
            className={`px-4 py-2 text-sm font-medium ${
              active === 'This Month'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            } ${active === 'This Month' ? '' : 'border-r border-gray-200'}`}
          >
            This Month
          </button>
          <button
            onClick={() => setActive('Past Sessions')}
            className={`px-4 py-2 text-sm font-medium ${
              active === 'Past Sessions'
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Past Sessions
          </button>
        </div>

        <div className="flex gap-3">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by patient name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-4 py-2 border border-gray-300 rounded-md w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <svg
              className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Status Filter */}
          <div className="w-full md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="All">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="missed">Missed</option>
            </select>
          </div>

          {/* Sort By */}
          {/* <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option>Upcoming</option>
            <option>Recent</option>
            <option>Patient Name</option>
          </select> */}
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <SessionsTableLoader />
      ) : sessions.length > 0 ? (
        <>
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Patient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Session Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session) => (
                    <tr key={session._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{session.date}</div>
                        <div className="text-sm text-gray-500">{session.startTime} - {session.endTime}</div>
                        { session.sessionType === 'monthly_checkin' && !session.monthlyAssessmentCompleted && session.status === 'scheduled' && (
                          <div className="text-sm text-red-600 mt-1">
                            * Monthly assessment not completed
                          </div>
                        )}
                        {!session.isSessionActive && session.status === 'scheduled' && (
                          <div className="text-sm text-gray-600 mt-1">
                            * Session will be active 10 minutes before start time
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{session.patientName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatSessionType(session.sessionType)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{session.sessionMode === 'video' ? 'Video Call' : 'Phone Call'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(session.status)}`}>
                          {formatStatus(session.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center space-x-2">
                            {session.status === 'scheduled' && session.sessionMode === 'video' && (
                              <div className="flex flex-col">
                                <button
                                  onClick={() => {
                                    handleStartSession(session);
                                  }}
                                  disabled={!session.isSessionActive || 
                                           (session.sessionType === 'monthly_checkin' && 
                                            !session.monthlyAssessmentCompleted && 
                                            session.status === 'scheduled') || 
                                           updatingSession === session._id}
                                  className={`flex items-center gap-1 ${
                                    !session.isSessionActive || 
                                    (session.sessionType === 'monthly_checkin' && 
                                     !session.monthlyAssessmentCompleted && 
                                     session.status === 'scheduled') || 
                                    updatingSession === session._id
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-blue-500 hover:bg-blue-600'
                                  } text-white px-3 py-1.5 rounded-md font-medium whitespace-nowrap`}
                                >
                                  {updatingSession === session._id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  ) : (
                                    <>
                                      <Video className="w-4 h-4" />
                                        Start Video Session
                                    </>
                                  )}
                                </button>
                               
                              </div>
                            )}

                            {session.status === 'in_progress' && session.sessionMode === 'video' && showingAddNotesFor !== session._id && (
                              <button
                                onClick={() => handleCompleteSession(session)}
                                disabled={updatingSession === session._id}
                                className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-md font-medium disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                {updatingSession === session._id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Complete
                                  </>
                                )}
                              </button>
                            )}

                            {showingAddNotesFor === session._id && (
                              <button
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowNotesPopup(true);
                                }}
                                className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-md font-medium whitespace-nowrap"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Add Session Notes
                              </button>
                            )}

                            {session.aiReviewed && (
                              <button
                                onClick={() => window.open(`/monthly-assessment-result/${session._id}`, '_blank')}
                                className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors whitespace-nowrap"
                              >
                                AI Results
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing page {pagination.currentPage} of {pagination.totalPages} 
                ({pagination.totalSessions} total sessions)
              </div>
              
              <nav className="inline-flex rounded-md shadow-sm">
                <button 
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {[...Array(pagination.totalPages)].map((_, idx) => {
                  const pageNumber = idx + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.currentPage === pageNumber
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button 
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="rounded-full bg-gray-100 p-3 mb-4">
            <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">No sessions found</h3>
          <p className="mt-1 text-sm text-gray-500">No sessions match your current filters.</p>
        </div>
      )}

      {/* Add Notes Popup */}
      {showNotesPopup && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Add Session Notes</h2>
              <button
                onClick={() => {
                  setShowNotesPopup(false);
                  setSelectedSession(null);
                  setSessionNotes('');
                  setShowingAddNotesFor(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="Enter your session notes here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="6"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowNotesPopup(false);
                  setSelectedSession(null);
                  setSessionNotes('');
                  setShowingAddNotesFor(null);
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes || !sessionNotes.trim()}
                className={`px-6 py-2 rounded-lg font-medium ${
                  savingNotes || !sessionNotes.trim()
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {savingNotes ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions; 